import React from 'react';
import PropTypes from 'prop-types';

/**
 * StatusBadge - کامپوننت نمایش وضعیت اقدام
 * 
 * @param {string} status - وضعیت اقدام (باز، در_حال_برنامه_ریزی، در_حال_انجام، بسته)
 * @param {string} className - کلاس‌های اضافی برای استایل
 * @param {boolean} showDot - نمایش نقطه رنگی در کنار متن
 * @param {string} size - اندازه badge (sm, md, lg)
 */
const StatusBadge = ({ status, className = '', showDot = true, size = 'md' }) => {
  if (!status) return null;

  const getStatusConfig = (statusValue) => {
    const statusStr = String(statusValue).toLowerCase();
    
    if (statusStr.includes('باز') || statusStr === 'open' || statusStr === 'باز') {
      return {
        bg: 'bg-gray-50 dark:bg-gray-800/50',
        text: 'text-gray-700 dark:text-gray-300',
        dot: 'bg-gray-500',
        border: 'border-gray-200 dark:border-gray-700',
      };
    }
    
    if (statusStr.includes('برنامه') || statusStr.includes('planning') || statusStr === 'در_حال_برنامه_ریزی') {
      return {
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        text: 'text-amber-700 dark:text-amber-400',
        dot: 'bg-amber-500',
        border: 'border-amber-200 dark:border-amber-800',
      };
    }
    
    if (statusStr.includes('انجام') || statusStr.includes('doing') || statusStr === 'در_حال_انجام') {
      return {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        text: 'text-blue-700 dark:text-blue-400',
        dot: 'bg-blue-500',
        border: 'border-blue-200 dark:border-blue-800',
      };
    }
    
    if (statusStr.includes('بسته') || statusStr.includes('closed') || statusStr === 'بسته') {
      return {
        bg: 'bg-emerald-50 dark:bg-emerald-900/20',
        text: 'text-emerald-700 dark:text-emerald-400',
        dot: 'bg-emerald-500',
        border: 'border-emerald-200 dark:border-emerald-800',
      };
    }
    
    // پیش‌فرض
    return {
      bg: 'bg-gray-50 dark:bg-gray-800/50',
      text: 'text-gray-700 dark:text-gray-300',
      dot: 'bg-gray-400',
      border: 'border-gray-200 dark:border-gray-700',
    };
  };

  // تعریف اندازه‌ها
  const sizeClasses = {
    sm: {
      container: 'px-2 py-0.5 text-xs gap-1',
      dot: 'w-1 h-1',
    },
    md: {
      container: 'px-2.5 py-1 text-xs gap-1.5',
      dot: 'w-1.5 h-1.5',
    },
    lg: {
      container: 'px-3 py-1.5 text-sm gap-2',
      dot: 'w-2 h-2',
    },
  };

  const config = getStatusConfig(status);
  const sizeConfig = sizeClasses[size] || sizeClasses.md;

  return (
    <span
      className={`
        inline-flex items-center font-medium rounded-full
        ${config.bg} ${config.text} ${config.border}
        border ${sizeConfig.container}
        transition-colors duration-200
        ${className}
      `}
      style={{ fontFamily: 'CustomFont' }}
    >
      {showDot && (
        <span
          className={`${config.dot} rounded-full flex-shrink-0 ${sizeConfig.dot}`}
        />
      )}
      <span className="whitespace-nowrap">{status}</span>
    </span>
  );
};

StatusBadge.propTypes = {
  status: PropTypes.string,
  className: PropTypes.string,
  showDot: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
};

export default StatusBadge;
